/**
 * useEarnings -- Earnings analysis store (Zustand).
 */
import { create } from 'zustand'
import {
  getEarningsCalendar,
  getEarningsHistory,
  getEarningsSurprise,
  getEarningsWhisper,
  getEarningsMovers,
} from '@/lib/api'
import type {
  EarningsCalendarEntry,
  EarningsHistoryResponse,
  EarningsSurpriseResponse,
  EarningsWhisperResponse,
  EarningsMoversResponse,
} from '@/lib/types'

interface EarningsState {
  // Calendar
  calendar: EarningsCalendarEntry[]
  calendarLoading: boolean

  // History (per ticker)
  history: EarningsHistoryResponse | null
  historyLoading: boolean

  // Surprise (per ticker)
  surprise: EarningsSurpriseResponse | null
  surpriseLoading: boolean

  // Whisper (per ticker)
  whisper: EarningsWhisperResponse | null
  whisperLoading: boolean

  // Movers
  movers: EarningsMoversResponse | null
  moversLoading: boolean

  error: string | null

  fetchCalendar: (days?: number) => Promise<void>
  fetchHistory: (ticker: string) => Promise<void>
  fetchSurprise: (ticker: string) => Promise<void>
  fetchWhisper: (ticker: string) => Promise<void>
  fetchMovers: () => Promise<void>
}

export const useEarnings = create<EarningsState>()((set) => ({
  calendar: [],
  calendarLoading: false,
  history: null,
  historyLoading: false,
  surprise: null,
  surpriseLoading: false,
  whisper: null,
  whisperLoading: false,
  movers: null,
  moversLoading: false,
  error: null,

  fetchCalendar: async (days = 14) => {
    set({ calendarLoading: true, error: null })
    try {
      const calendar = await getEarningsCalendar(days)
      set({ calendar, calendarLoading: false })
    } catch (e) {
      set({ error: String(e), calendarLoading: false })
    }
  },

  fetchHistory: async (ticker: string) => {
    set({ historyLoading: true, error: null })
    try {
      const history = await getEarningsHistory(ticker)
      set({ history, historyLoading: false })
    } catch (e) {
      set({ error: String(e), historyLoading: false })
    }
  },

  fetchSurprise: async (ticker: string) => {
    set({ surpriseLoading: true, error: null })
    try {
      const surprise = await getEarningsSurprise(ticker)
      set({ surprise, surpriseLoading: false })
    } catch (e) {
      set({ error: String(e), surpriseLoading: false })
    }
  },

  fetchWhisper: async (ticker: string) => {
    set({ whisperLoading: true, error: null })
    try {
      const whisper = await getEarningsWhisper(ticker)
      set({ whisper, whisperLoading: false })
    } catch (e) {
      set({ error: String(e), whisperLoading: false })
    }
  },

  fetchMovers: async () => {
    set({ moversLoading: true, error: null })
    try {
      const movers = await getEarningsMovers()
      set({ movers, moversLoading: false })
    } catch (e) {
      set({ error: String(e), moversLoading: false })
    }
  },
}))
